import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import ticketApprovalsApi from '../../../middleware/api/Support/TicketApprovals/ticketApprovalsApi'
import { RootState } from '../../../stateStore'
import { ValidationError } from '../../../types/commonTypes'
import {
  DepartmentCategoryList,
  DepartmentList,
  GetAllLookUps,
  GetAllTicketsForApprovalProps,
  GetAllTicketsForApprovalResponse,
  SubCategoryList,
  TicketApprovalsSliceState,
  TrackerList,
} from '../../../types/Support/TicketApprovals/ticketApprovalsTypes'

const getDepartmentNameList = createAsyncThunk(
  'ticketApprovals/getDepartmentNameList',
  async (_, thunkApi) => {
    try {
      return await ticketApprovalsApi.getDepartmentNameList()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getTrackerList = createAsyncThunk(
  'ticketApprovals/getTrackerList',
  async (_, thunkApi) => {
    try {
      return await ticketApprovalsApi.getTrackerList()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getAllLookUps = createAsyncThunk(
  'ticketApprovals/getAllLookUps',
  async (_, thunkApi) => {
    try {
      return await ticketApprovalsApi.getAllLookUps()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getDepartmentCategoryList = createAsyncThunk(
  'ticketApprovals/getDepartmentCategoryList',
  async (deptId: number, thunkApi) => {
    try {
      return await ticketApprovalsApi.getDepartmentCategoryList(deptId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getSubCategoryList = createAsyncThunk(
  'ticketApprovals/getSubCategoryList',
  async (categoryId: number, thunkApi) => {
    try {
      return await ticketApprovalsApi.getSubCategoryList(categoryId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getAllTicketsForApproval = createAsyncThunk(
  'ticketApprovals/getAllTicketsForApproval',
  async (props: GetAllTicketsForApprovalProps, thunkApi) => {
    try {
      return await ticketApprovalsApi.getAllTicketsForApproval(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const rejectTicket = createAsyncThunk(
  'ticketApprovals/rejectTicket',
  async (ticketId: number, thunkApi) => {
    try {
      return await ticketApprovalsApi.rejectTicket(ticketId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getActiveEmployeeListThunk = createAsyncThunk(
  'ticketApprovals/getActiveEmployeeListThunk',
  async (_, thunkApi) => {
    try {
      return await ticketApprovalsApi.getActiveEmployeeList()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const initialTicketApprovalsSliceState: TicketApprovalsSliceState = {
  isLoading: ApiLoadingState.idle,
  listSize: 0,
  departmentNameList: [],
  trackerList: [],
  departmentCategoryList: [],
  subCategoryList: [],
  ticketsForApproval: { size: 0, list: [] },
  getAllLookUps: [],
  selectedTicketId: 0,
  toggleValue: '',
  routePath: '',
  mealType: {} as DepartmentCategoryList,

  searchValue: '',
  TicketStatusValue: 'New',
  ApprovalStatusValue: 'Pending Approval',
  DepartmentNameValue: 0,
  CategoryNameValue: 0,
  SubCategoryNameValue: 0,
  DateValue: 'Today',
  TrackerValue: 0,
  FormDate: '',
  ToDate: '',

  employeeNameCheck: false,
  assigneeNameCheck: false,

  previousSelectedFilterOptions: {
    categoryId: '',
    dateSelection: 'Today',
    departmentId: '',
    multiSearch: '',
    progressStatus: 'New',
    searchByAssigneeName: false,
    searchByEmpName: false,
    subCategoryId: '',
    ticketStatus: 'Pending Approval',
    fromDate: '',
    toDate: '',
    trackerID: '',
    startIndex: 0,
    endIndex: 20,
  },
  ticketApprovalsUpdated: false,
}

const ticketApprovalsSlice = createSlice({
  name: 'ticketApprovals',
  initialState: initialTicketApprovalsSliceState,
  reducers: {
    selectTicketId: (state, action) => {
      state.selectedTicketId = action.payload
    },
    setToggle: (state, action) => {
      state.toggleValue = action.payload
    },
    setRoutePath: (state, action) => {
      state.routePath = action.payload
    },
    clearCategory: (state) => {
      state.departmentCategoryList = []
    },
    clearSubCategory: (state) => {
      state.subCategoryList = []
    },

    setSearchValue: (state, action) => {
      state.searchValue = action.payload
    },
    setTicketStatusValue: (state, action) => {
      state.TicketStatusValue = action.payload
    },
    setApprovalStatusValue: (state, action) => {
      state.ApprovalStatusValue = action.payload
    },
    setDepartmentNameValue: (state, action) => {
      state.DepartmentNameValue = action.payload
    },
    setCategoryNameValue: (state, action) => {
      state.CategoryNameValue = action.payload
    },
    setSubCategoryNameValue: (state, action) => {
      state.SubCategoryNameValue = action.payload
    },
    setDateValue: (state, action) => {
      state.DateValue = action.payload
    },
    setTrackerValue: (state, action) => {
      state.TrackerValue = action.payload
    },
    setFormDataValue: (state, action) => {
      state.FormDate = action.payload
    },
    setToDateValue: (state, action) => {
      state.ToDate = action.payload
    },
    setEmployeeNameCheckbox: (state, action) => {
      state.employeeNameCheck = action.payload
    },
    setAssigneeNameCheckbox: (state, action) => {
      state.assigneeNameCheck = action.payload
    },
    setPreviousSelectedFilterOptions: (state, action) => {
      state.previousSelectedFilterOptions = action.payload
    },
    setTicketApprovalsUpdated: (state, action) => {
      state.ticketApprovalsUpdated = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDepartmentNameList.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.departmentNameList = action.payload
      })
      .addCase(getTrackerList.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.trackerList = action.payload
      })
      .addCase(getDepartmentCategoryList.fulfilled, (state, action) => {
        state.departmentCategoryList = action.payload
      })
      .addCase(getSubCategoryList.fulfilled, (state, action) => {
        state.subCategoryList = action.payload
      })
      .addCase(getAllTicketsForApproval.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.ticketsForApproval = action.payload
        state.listSize = action.payload.size
      })
      .addCase(getAllLookUps.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.getAllLookUps = action.payload
      })
      .addCase(rejectTicket.fulfilled, (state) => {
        state.isLoading = ApiLoadingState.succeeded
      })
      .addCase(getAllTicketsForApproval.rejected, (state) => {
        state.isLoading = ApiLoadingState.failed
      })
      .addMatcher(
        isAnyOf(
          getDepartmentNameList.pending,
          getTrackerList.pending,
          getAllTicketsForApproval.pending,
          getAllLookUps.pending,
          rejectTicket.pending,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.loading
        },
      )
  },
})

const ticketApprovalsThunk = {
  getDepartmentNameList,
  getTrackerList,
  getDepartmentCategoryList,
  getSubCategoryList,
  getAllTicketsForApproval,
  getAllLookUps,
  rejectTicket,
  getActiveEmployeeListThunk,
}

const isLoading = (state: RootState): ApiLoadingState =>
  state.ticketApprovals.isLoading

const listSize = (state: RootState): number => state.ticketApprovals.listSize

const departmentNameList = (state: RootState): DepartmentList[] =>
  state.ticketApprovals.departmentNameList

const mealType = (state: RootState): DepartmentCategoryList =>
  state.ticketApprovals.mealType

const departmentCategoryList = (state: RootState): DepartmentCategoryList[] =>
  state.ticketApprovals.departmentCategoryList

const subCategoryList = (state: RootState): SubCategoryList[] =>
  state.ticketApprovals.subCategoryList

const trackerList = (state: RootState): TrackerList[] =>
  state.ticketApprovals.trackerList

const allLookUps = (state: RootState): GetAllLookUps[] =>
  state.ticketApprovals.getAllLookUps

const selectTicketId = (state: RootState): number =>
  state.ticketApprovals.selectedTicketId

const toggleValue = (state: RootState): string =>
  state.ticketApprovals.toggleValue

const routePath = (state: RootState): string => state.ticketApprovals.routePath

const TicketStatusValue = (state: RootState): string =>
  state.ticketApprovals.TicketStatusValue

const ApprovalStatusValue = (state: RootState): string =>
  state.ticketApprovals.ApprovalStatusValue

const DepartmentNameValue = (state: RootState): number =>
  state.ticketApprovals.DepartmentNameValue

const CategoryNameValue = (state: RootState): number =>
  state.ticketApprovals.CategoryNameValue

const SubCategoryNameValue = (state: RootState): number =>
  state.ticketApprovals.SubCategoryNameValue

const TrackerValue = (state: RootState): number =>
  state.ticketApprovals.TrackerValue

const DateValue = (state: RootState): string => state.ticketApprovals.DateValue

const FormDateValue = (state: RootState): string | Date =>
  state.ticketApprovals.FormDate

const ToDateValue = (state: RootState): string | Date =>
  state.ticketApprovals.ToDate
const SearchValue = (state: RootState): string =>
  state.ticketApprovals.searchValue

const employeeNameCheck = (state: RootState): boolean =>
  state.ticketApprovals.employeeNameCheck
const assigneeNameCheck = (state: RootState): boolean =>
  state.ticketApprovals.assigneeNameCheck
const previousSelectedFilterOptions = (
  state: RootState,
): GetAllTicketsForApprovalProps =>
  state.ticketApprovals.previousSelectedFilterOptions
const ticketApprovalsUpdated = (state: RootState): boolean =>
  state.ticketApprovals.ticketApprovalsUpdated

const ticketsForApproval = (
  state: RootState,
): GetAllTicketsForApprovalResponse => state.ticketApprovals.ticketsForApproval

const ticketApprovalsSelectors = {
  listSize,
  departmentNameList,
  departmentCategoryList,
  subCategoryList,
  trackerList,
  ticketsForApproval,
  isLoading,
  allLookUps,
  selectTicketId,
  toggleValue,
  routePath,
  mealType,

  SearchValue,
  TicketStatusValue,
  ApprovalStatusValue,
  DepartmentNameValue,
  CategoryNameValue,
  SubCategoryNameValue,
  DateValue,
  TrackerValue,
  FormDateValue,
  ToDateValue,
  employeeNameCheck,
  assigneeNameCheck,
  previousSelectedFilterOptions,
  ticketApprovalsUpdated,
}

export const ticketApprovalsService = {
  ...ticketApprovalsThunk,
  actions: ticketApprovalsSlice.actions,
  selectors: ticketApprovalsSelectors,
}

export default ticketApprovalsSlice.reducer
